import { Link } from "@tanstack/react-router";
import type { PluginSkillPermissions } from "@cmmd-forge/contracts/settings";
import { CheckIcon } from "lucide-react";

import { Switch } from "../ui/switch";
import { PluginIcon } from "./pluginIcons";
import type { PluginDetailMetadata, PluginSkillMetadata } from "./pluginMetadata";
import { isPluginSkillEnabled } from "./pluginPermissions";
import type { ForgePlugin } from "./pluginsPage.logic";

export interface ManagePluginRow {
  readonly metadata: PluginDetailMetadata;
  readonly plugin: ForgePlugin;
  readonly enabled: boolean;
  readonly connected: boolean;
}

export interface ManageSkillRow {
  readonly id: string;
  readonly metadata: PluginDetailMetadata;
  readonly skill: PluginSkillMetadata;
}

interface ManagePluginRowsProps {
  readonly rows: readonly ManagePluginRow[];
  readonly onPluginEnabledChange: (row: ManagePluginRow, enabled: boolean) => void;
}

export function ManagePluginRows({ rows, onPluginEnabledChange }: ManagePluginRowsProps) {
  return (
    <div className="space-y-2">
      {rows.length > 0 ? (
        rows.map((row) => (
          <ManagePluginRowView
            key={row.metadata.id}
            row={row}
            onEnabledChange={(enabled) => onPluginEnabledChange(row, enabled)}
          />
        ))
      ) : (
        <p className="py-10 text-sm text-muted-foreground">No plugins match your search.</p>
      )}
    </div>
  );
}

interface ManagePluginRowViewProps {
  readonly row: ManagePluginRow;
  readonly onEnabledChange: (enabled: boolean) => void;
}

function ManagePluginRowView({ row, onEnabledChange }: ManagePluginRowViewProps) {
  return (
    <div className="grid min-h-20 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-4 rounded-md px-2 py-3 transition-colors hover:bg-muted/25">
      <Link
        to="/plugins/$pluginId"
        params={{ pluginId: row.metadata.id }}
        className="flex size-11 items-center justify-center rounded-md border border-border/80 bg-card shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={`Open ${row.metadata.title}`}
      >
        <PluginIcon pluginId={row.metadata.id} className="size-7" />
      </Link>
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <Link
            to="/plugins/$pluginId"
            params={{ pluginId: row.metadata.id }}
            className="truncate text-sm font-medium text-foreground outline-none hover:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
          >
            {row.metadata.title}
          </Link>
          <span className="text-xs text-muted-foreground">{row.metadata.category}</span>
        </div>
        <p className="truncate text-sm text-muted-foreground">{row.metadata.appDescription}</p>
      </div>
      {row.connected ? (
        <CheckIcon className="mx-3 size-4 text-muted-foreground" aria-label="Connected" />
      ) : (
        <Switch
          checked={row.enabled}
          aria-label={`${row.metadata.title} skills enabled`}
          onCheckedChange={onEnabledChange}
        />
      )}
    </div>
  );
}

interface ManageSkillRowsProps {
  readonly skills: readonly ManageSkillRow[];
  readonly permissions: PluginSkillPermissions;
  readonly onSkillEnabledChange: (
    metadata: PluginDetailMetadata,
    skillId: string,
    enabled: boolean,
  ) => void;
}

export function ManageSkillRows({
  skills,
  permissions,
  onSkillEnabledChange,
}: ManageSkillRowsProps) {
  return (
    <div className="space-y-2">
      {skills.length > 0 ? (
        skills.map((entry) => (
          <div
            key={entry.id}
            className="grid min-h-16 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-4 rounded-md px-2 py-3 transition-colors hover:bg-muted/25"
          >
            <div className="flex size-10 items-center justify-center rounded-md border border-border/80 bg-card shadow-sm">
              <PluginIcon pluginId={entry.metadata.id} className="size-6" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground">{entry.skill.name}</p>
              <p className="truncate text-sm text-muted-foreground">{entry.skill.description}</p>
            </div>
            <Switch
              checked={isPluginSkillEnabled(permissions, entry.metadata.id, entry.skill.id)}
              aria-label={`${entry.skill.name} enabled`}
              onCheckedChange={(enabled) =>
                onSkillEnabledChange(entry.metadata, entry.skill.id, enabled)
              }
            />
          </div>
        ))
      ) : (
        <p className="py-10 text-sm text-muted-foreground">No skills match your search.</p>
      )}
    </div>
  );
}
